import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, IconButton } from '@mui/material';

import { useCreateLabel } from '../../shared';

const COLOR_OPTIONS = [
    { id: 'default', name: 'Default', color: '#CCCCCC' },
    { id: 'gray', name: 'Gray', color: '#E9E9E9' },
    { id: 'brown', name: 'Brown', color: '#CBAA9E' },
    { id: 'orange', name: 'Orange', color: '#FFCD83' },
    { id: 'yellow', name: 'Yellow', color: '#FFF59D' },
    { id: 'green', name: 'Green', color: '#C0E893' },
    { id: 'blue', name: 'Blue', color: '#9DD3FF' },
    { id: 'purple', name: 'Purple', color: '#E4ADEE' },
    { id: 'pink', name: 'Pink', color: '#FFBED4' },
    { id: 'red', name: 'Red', color: '#FF9C9C' },
];

function getPaperPosition(anchor: any = 'right') {
    if (anchor === 'left') {
        return { position: 'fixed', left: 'calc(50% - 380px)', top: 'calc(50% - 200px)' };
    }
    if (anchor === 'right') {
        return { position: 'fixed', left: 'calc(50% + 200px)', top: 'calc(50% - 200px)' };
    }
    return {};
}

function LabelCreatorDialog({
    open,
    onClose,
    initialName = '',
    anchor = 'right',
    onCreated = (_label?: any) => {},
    hideBackdrop = true,
    tKey,
}: any) {
    const { t } = useTranslation(undefined, { keyPrefix: tKey });
    const [name, setName] = useState(initialName);
    const [selectedColor, setSelectedColor] = useState(COLOR_OPTIONS[0].color);
    const createLabel = useCreateLabel();
    const submitting = createLabel.isPending;

    useEffect(() => {
        setName(initialName);
    }, [initialName]);

    useEffect(() => {
        if (open) {
            setSelectedColor(COLOR_OPTIONS[0].color);
        }
    }, [open]);

    const handleCreate = () => {
        const trimmed = name.trim();
        if (!trimmed || submitting) return;
        createLabel.mutate({ name: trimmed, color: selectedColor }, { onSuccess: onCreated });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            hideBackdrop={hideBackdrop}
            PaperProps={{
                sx: {
                    width: '240px',
                    maxWidth: '240px',
                    borderRadius: '16px',
                    p: 0,
                    overflow: 'hidden',
                    ...getPaperPosition(anchor),
                },
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                <div className='flex h-full flex-col bg-white p-2'>
                    <div className='flex items-center rounded-full border border-[#DFE1E6] px-2 py-1'>
                        <input
                            value={name}
                            onChange={(e: any) => setName(e.target.value)}
                            className='w-full border-none bg-transparent text-sm text-[#53545C] outline-none'
                        />
                        <IconButton
                            size='small'
                            className='text-[#3c3c3c]'
                            onClick={handleCreate}
                            disabled={submitting || !name.trim()}
                        >
                            <img src='/add-label.svg' alt='Add label icon' className='h-4 w-4' />
                        </IconButton>
                    </div>

                    <div className='mt-3 flex flex-col gap-1 px-2'>
                        <span className='text-xs text-[#6B7280]'>{t('labelPreview')}</span>
                        <span
                            className='self-start inline-flex min-w-[60px] rounded-md px-2 py-1 text-[14px] font-normal leading-[1.2] text-[#3c3c3c] break-words whitespace-normal w-auto'
                            style={{ backgroundColor: selectedColor }}
                        >
                            {name || 'Label'}
                        </span>
                    </div>

                    <button
                        type='button'
                        onClick={onClose}
                        className='mt-3 flex items-center gap-2 rounded-md px-2 py-1 text-sm font-medium text-[#3c3c3c] hover:bg-[#F4F4F5]'
                    >
                        <img src='/delete.svg' alt='Delete icon' className='h-[14px] w-[12px] opacity-80' />
                        <span className='text-[14px] font-normal text-[#53545C]'>{t('deleteLabel')}</span>
                    </button>

                    <hr className='my-2 border-t border-[#E5E7EB]' />
                    <div className='pl-2 text-[14px] font-normal text-[#53545C]'>{t('colors')}</div>
                    <div className='mt-2 ml-2 flex flex-col gap-2'>
                        {COLOR_OPTIONS.map((option: any) => {
                            const isSelected = option.color === selectedColor;
                            return (
                                <button
                                    key={option.id}
                                    type='button'
                                    onClick={() => setSelectedColor(option.color)}
                                    className='flex items-center gap-2 text-left'
                                >
                                    <span
                                        className='h-[18px] w-[18px] rounded-[7px] border border-[#D1D5DB]'
                                        style={{ backgroundColor: option.color }}
                                    />
                                    <span className='text-sm text-[#3c3c3c]'>{option.name}</span>
                                    {isSelected && (
                                        <span className='ml-auto text-xs text-[#4B5563]'>{t('selected')}</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default LabelCreatorDialog;
